import React, { useCallback, useEffect, useState, useRef, Suspense, lazy } from 'react';
import './BusinessSegment.css';
import { useParams, useLocation } from 'react-router-dom';
import * as FeatherIcon from 'react-feather';
import { 
  getUserStatsObject,
  recordActivity,
  getUrlDetailsObj,
  getTabType
} from '../../../../services/commonService';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBusinessUnitAccDetailRequest, fetchBusinessUnitAccDetailReset } from '../../../../store/features/actions/businessUnitAccountDetailsActions';
import BusinessUnitOverviewSkeleton from '../../skeletons/businessUnitOverviewSkeleton/businessUnitOverviewSkeleton';
import GenericTableSkeleton from '../../skeletons/tableSkeleton/genericTableSkeleton';
import { fetchAccBuNudgeRequest } from '../../../../store/features/actions/accountBuNudgesActions';
import { fetchCompBuFinancialRequest } from '../../../../store/features/actions/companyBuFinancialsActions';
import BusinessUnitOverview from './BusinessUnitOverview';
import BuKeyFinancials from './BuKeyFinancials';
import NudgesWithPrompts from '../Nudges/NudgesWithPrompts';
import BarGraphSkeleton from '../../skeletons/barGraphSkeleton/barGraphSkeleton';

// Lazy load the components
const GrowthOpportunities = lazy(() => import('./GrowthOpportunities'));
const KeyCompetitors = lazy(() => import('./KeyCompetitors'));
const KeyCustomerSegments = lazy(() => import('./KeyCustomerSegments'));
const McpPainpoints = lazy(() => import('./McpPainpoints'));
const KeyTrendsAndDisruptions = lazy(() => import('./KeyTrendsAndDisruptions'));
const Contacts = lazy(() => import('../Contact/Contacts'));


export default function BusinessSegments({ active_tab_key, account_contacts_data }) {
  const dispatch = useDispatch();
  const { account_info } = useSelector(state => state?.accountsReducer);
  const [business_units, setBusinessUnits] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [activeUnit, setActiveUnit] = useState('');
  const [activeUnitInfo, setActiveUnitInfo] = useState([]);
  const tabContainerRef = useRef(null); // Reference for tab container
  const { acc_bu_details, acc_bu_details_loading } = useSelector(state => state.businessUnitAccDetailsReducer);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [showArrows, setShowArrows] = useState(false); // New state to control arrow visibility

  const { accountId, accountName, clientName } = useParams();
  const [activeAccordion, setActiveAccordion] = useState(0); 
  const {data,loading,error} = useSelector(state=>state?.companyBuFinancialsReducer)
  const [contactsData, setContactsData] = useState({});

  var pageParsedData = getUrlDetailsObj(useLocation, useParams);
  pageParsedData.query_params = {};
  pageParsedData.query_params.tab = 'business';
  var tab_name = getTabType('business') || "";

  useEffect(() => {
    if (active_tab_key === 'business') {
      updateScrollButtons();
    }
  }, [active_tab_key]);

  const updateBusinessUnits = () => {
    const business_units = account_info?.business_units || [];
    setBusinessUnits(business_units);
    const bu_tabs = business_units.map((unit) => unit.name);
    setTabs(bu_tabs);
    setActiveUnit(bu_tabs[0]);
  };

  useEffect(() => {
    updateBusinessUnits();
  }, [account_info, accountId, accountName, clientName]);

  const fetchSegmentData = useCallback(async (bu_unit) => {
    dispatch(fetchBusinessUnitAccDetailRequest({ accountName, clientName, bu_unit }));
    dispatch(fetchAccBuNudgeRequest({ accountName, clientName, bu_unit }));
    dispatch(fetchCompBuFinancialRequest({ accountId, accountName, bu_unit }));
  }, [accountName, accountId, clientName]);

  useEffect(() => {
    setActiveUnitInfo(acc_bu_details);
  }, [acc_bu_details_loading]);

  useEffect(() => {
    let userStat = getUserStatsObject(
      tab_name + " - " + activeUnit,
      "bu_change",
      "Page Source: " + accountName,
      window.location.href,
      accountId
    );
    userStat.url_details = pageParsedData;
    recordActivity(userStat);
    dispatch(fetchBusinessUnitAccDetailReset())
    if (activeUnit) {
      fetchSegmentData(activeUnit);
    }
  }, [activeUnit, fetchSegmentData]);

  useEffect(() => {
    let bu_contacts = account_contacts_data?.records?.filter((contact) => contact?.business_segment === activeUnit);
    const contactsData = {};
    contactsData.filters = account_contacts_data.filters || {};
    contactsData.records = bu_contacts || [];
    setContactsData(contactsData);
  }, [activeUnit, account_contacts_data]);

  const updateScrollButtons = () => {
    const tabContainer = tabContainerRef.current;
    if (tabContainer) {
      const canScrollLeftValue = tabContainer.scrollLeft > 0;
      const canScrollRightValue = tabContainer.scrollLeft + tabContainer.clientWidth < tabContainer.scrollWidth;

      setCanScrollLeft(canScrollLeftValue);
      setCanScrollRight(canScrollRightValue);

      // Show arrows only if the content is scrollable
      setShowArrows(tabContainer.scrollWidth > tabContainer.clientWidth);
    }
  };

  const scrollLeft = () => {
    const tabContainer = tabContainerRef.current;
    if (tabContainer) {
      tabContainer.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    const tabContainer = tabContainerRef.current;
    if (tabContainer) {
      tabContainer.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    setActiveAccordion('0'); // Set first accordion to open
  }, [activeUnit]);

  useEffect(() => {
    updateScrollButtons(); // Initially check if scrolling is needed
    const tabContainer = tabContainerRef.current;
    
    if (tabContainer) {
      tabContainer.addEventListener('scroll', updateScrollButtons);
      window.addEventListener('resize', updateScrollButtons); // Recheck on window resize
    }

    return () => {
      if (tabContainer) {
        tabContainer.removeEventListener('scroll', updateScrollButtons);
      }
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [tabs]);

  if (active_tab_key !== 'business') {
    return <></>;
  }

  if (!account_info || !business_units.length) {
    return <> </>;
  }

  return (
    <div>
      <div className="business_seg_tabs">
        <div className="navbar navbar-expand-lg container-fluid custom_subnavbar">
          {showArrows && (
            <>
              <button
                className="scroll-btn left-arrow"
                onClick={scrollLeft}
                disabled={!canScrollLeft}
              >
                <FeatherIcon.ArrowLeftCircle className='icons_table mr-1' />
              </button>
            </>
          )}
          <ul className="nav nav-tabs custom_nav_tab" ref={tabContainerRef}>
            {tabs.map((tab) => (
              <li className="nav-item" key={`seg_unit_${tab}`}>
                <a
                  className={`nav-link ${activeUnit === tab ? 'active' : ''}`}
                  onClick={() => setActiveUnit(tab)}
                >
                  {tab}
                </a>
              </li>
            ))}
          </ul>
          {showArrows && ( // Conditionally render arrows based on `showArrows` state
            <>
              <button
                className="scroll-btn right-arrow"
                onClick={scrollRight}
                disabled={!canScrollRight}
              >
                <FeatherIcon.ArrowRightCircle className='icons_table mr-1' />
              </button>
            </>
          )}
        </div>
      </div>
      {activeUnit && 
        <div className='container-fluid mt-4'>
          <div className="row">

          <div className="col-md-12 generic-take-space-parent">
              {acc_bu_details_loading ? (
                <div className="generic-take-space-child mb-2">
                  <BusinessUnitOverviewSkeleton />{" "}
                </div>
              ) : (
                <BusinessUnitOverview
                  bu_unit={activeUnit}
                  acc_bu_details={activeUnitInfo}
                  keyFinData={data?.data}
                />
              )}

              {loading ? (
                <div className="generic-take-space-child ">
                  <div className='custom_card_block pb-4'>
                  <BarGraphSkeleton />{" "}
                  </div>
                </div>
              ) : (
                <BuKeyFinancials
                  bu_unit={activeUnit}
                  data={data}
                  loading={loading}
                  error={error}
                />
              )}
            </div>
            <div className='col-md-12'>
              <Suspense fallback={null}>
                {acc_bu_details_loading ? <GenericTableSkeleton rowCount={5} colCount={1} /> : <McpPainpoints bu_unit={activeUnit} acc_bu_details={activeUnitInfo} activeAccordion={activeAccordion} setActiveAccordion={setActiveAccordion} />}
              </Suspense>
            </div>

            <div className='col-md-12'>
              <Suspense fallback={null}>
                {acc_bu_details_loading ? <GenericTableSkeleton rowCount={5} colCount={6} /> : <GrowthOpportunities bu_unit={activeUnit} acc_bu_details={activeUnitInfo} active_tab_key={active_tab_key} />}
              </Suspense>
            </div>

            <div className='col-md-12'>
              <Suspense fallback={null}>
                <NudgesWithPrompts active_tab_key={active_tab_key} isBU={true}/>
              </Suspense>
            </div>

            {/* <div className='col-md-12'>
              <Suspense fallback={null}>
               {account_info?.business_units && <Prompters bu_unit={activeUnit} active_tab_key={active_tab_key} />}
              </Suspense>
            </div> */}

            <div className='col-md-12'>
              <Suspense fallback={null}>
               <Contacts bu_unit={activeUnit} active_tab_key={active_tab_key} account_contacts_data={contactsData} />
              </Suspense>
            </div>

            <div className='col-md-12'>
              <Suspense fallback={null}>
                <KeyTrendsAndDisruptions bu_unit={activeUnit} keyTrendsAndDisruptionsData={activeUnitInfo?.account_bu_futuristic_trends || []} />
              </Suspense>
            </div>    
            <div className='col-md-12'>
              <Suspense fallback={null}>
                <KeyCustomerSegments bu_unit={activeUnit} customer_segment_and_key_customers={activeUnitInfo?.customer_segment_and_key_customers || []} />
              </Suspense>
            </div>
            <div className='col-md-12'>
              <Suspense fallback={null}>
                <KeyCompetitors bu_unit={activeUnit} account_bu_key_competitors={activeUnitInfo?.account_bu_key_competitors || []} />
              </Suspense>
            </div>
          </div>         
        </div>
      }
    </div>
  );
}